import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { getActivation, getActivationRecipe, getActivations, queryKeys } from "@/api";
import { useActivationContext, useActivationActions } from "@/context";
import { useToken } from "@/hooks/core";
import { useClient } from "@/hooks/useClient";
import { defaultActivation, unwrap } from "@/utils";

/**
 * The activation being viewed or edited. A saved activation is fetched through
 * the cache and loaded into the audience context once; without an id it's a
 * fresh default activation.
 */
export const useActivation = (campaignId: string | undefined) => {
  const { currentActivation } = useActivationContext();
  const { load } = useActivationActions();
  const getToken = useToken();

  const needsLoad = !!campaignId && currentActivation?.campaign_id !== campaignId;

  const { data, error, isFetching } = useQuery({
    queryKey: queryKeys.activationDetail(campaignId ?? ""),
    queryFn: async () => unwrap(await getActivation(await getToken(), campaignId!)),
    enabled: needsLoad,
  });

  useEffect(() => {
    if (!campaignId) {
      load(defaultActivation);
    }
  }, [campaignId, load]);

  useEffect(() => {
    if (needsLoad && data && !isFetching && !error) {
      load(data);
    }
  }, [data, error, isFetching, load, needsLoad]);

  return {
    activation: needsLoad ? null : currentActivation,
    loading: needsLoad && !error,
    error: error?.message ?? null,
  };
};

export const useActivations = () => {
  const getToken = useToken();
  const client = useClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.activations(client),
    queryFn: async () => unwrap(await getActivations(await getToken(), client)) ?? [],
  });

  return {
    activations: data ?? null,
    loading: isLoading,
    error: error?.message ?? null,
  };
};

const NO_CATEGORIES: never[] = [];

/**
 * An activation's Meta recipe. The activation is loaded into the audience
 * context each time the latest recipe arrives, as the recipe page edits it.
 */
export const useActivationRecipe = (campaignId: string | undefined) => {
  const { currentActivation } = useActivationContext();
  const { load } = useActivationActions();
  const getToken = useToken();

  const { data, error, isFetching } = useQuery({
    queryKey: queryKeys.activationRecipe(campaignId ?? ""),
    queryFn: async () => unwrap(await getActivationRecipe(await getToken(), campaignId!)),
    enabled: !!campaignId,
    staleTime: 0, // The recipe page always starts from the saved recipe
  });

  const loaded = !isFetching ? data?.activation : undefined;
  useEffect(() => {
    if (loaded) load(loaded);
  }, [load, loaded]);

  const ready = !!data && currentActivation?.campaign_id === campaignId;

  return {
    activation: ready ? currentActivation : null,
    categories: data?.categories ?? NO_CATEGORIES,
    interests: data?.interests ?? NO_CATEGORIES,
    loading: !ready && !error,
    error: error?.message ?? null,
  };
};
